import _ from 'underscore'
import React, { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import moment from 'moment-timezone'

import AwairInfoPanel from 'pages/Device/AwairRthPanel/AwairInfoPanel'
import LegendGroup from 'pages/Device/AwairRthPanel/LegendGroup'

import * as formats from 'helpers/formatters'

import { color } from 'styles/colors'

// NOTE: dashSequence works like this:
// example: 8 3 3 3 3 3 gives you a dash, dot, dot pattern
// 8 pixels of solid
// 3 pixels of space
// 3 pixels of solid
// 3 pixels of space
// 3 pixels of solid
// 3 pixels of space <- add space at end so pattern can repeat
//
// you can use the tool found here to help make a new pattern: https://kovart.github.io/dashed-border-generator/

const SOLID = '1, 0'
const DASH_DOT_DOT = '8, 3, 3, 3, 3, 3'

export const SECTIONS = [
  {
    title: 'Awair Device',
    name: 'awair',
    items: [
      { seriesName: 'temp', displayValue: 'Temperature', units: formats.degreesFahrenheit, dashSequence: SOLID },
      { seriesName: 'humidity', displayValue: 'Humidity', units: formats.percentage, dashSequence: SOLID },
      { seriesName: 'co2', displayValue: 'CO2', units: formats.partsPerMillion, dashSequence: SOLID },
      { seriesName: 'voc', displayValue: 'Chemical', units: formats.partsPerBillion, dashSequence: SOLID },
      { seriesName: 'pm25', displayValue: 'PM2.5', units: formats.microgramsPerCubicMeter, dashSequence: SOLID }
    ]
  },
  {
    title: 'Indoor Unit',
    name: 'indoorUnit',
    items: [
      { seriesName: 'actualAirflow', displayValue: 'Actual Airflow', units: formats.cubicFeetPerMinute, dashSequence: DASH_DOT_DOT }
    ]
  }
]

interface SelectedLegendItems {
  temp: boolean,
  humidity: boolean,
  co2: boolean,
  voc: boolean,
  pm25: boolean,
  actualAirflow: boolean
}

type Props = {
  selectedLegendItems: SelectedLegendItems
  setSelectedLegendItemsCallback: (selectedLegendItems: SelectedLegendItems | ((prev: SelectedLegendItems) => SelectedLegendItems)) => void
  dataAtCursor: Array<Plotly.PlotDatum> | null
  timeAtCursor: number | null
  timeZone: string
  isChartHovered?: boolean
  displayIndoorUnit?: boolean
}

const Legend = ({
  selectedLegendItems,
  setSelectedLegendItemsCallback,
  dataAtCursor,
  isChartHovered = false,
  timeAtCursor,
  timeZone,
  displayIndoorUnit = false
}: Props) => {
  const [filteredSections, setFilteredSections] = useState(SECTIONS)
  useEffect(() => {
    if (!displayIndoorUnit) {
      setFilteredSections(SECTIONS.filter(section => section.name !== 'indoorUnit'))
    }
  }, [displayIndoorUnit])

  return (
    <>
      <S.Legend isChartHovered={isChartHovered} aria-label="chart legend">
        <S.Time>{timeAtCursor ? moment(timeAtCursor).tz(timeZone || moment.tz.guess()).format('h:mm A') : null}</S.Time>

        {_.map(filteredSections, (section) => (
          <S.LegendGroup {...{ section, selectedLegendItems, setSelectedLegendItemsCallback, dataAtCursor }} key={section.title} />
        ))}
      </S.Legend>

      <S.AwairInfoPanel />
    </>
  )
}

export default Legend

const bottomPadding = '15px'

const S = {
  Legend: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isChartHovered'].includes(prop)
  })<{isChartHovered: boolean}>`
    padding: 36px 20px ${bottomPadding};
    border: 3px solid ${color('paneBackground')};
    ${({ isChartHovered }) => {
    if (isChartHovered) {
      return css`
        position: relative;
        margin: -26px 5px 0 5px;
        padding: 27px 15px ${bottomPadding};
        border-radius: 5px;
        background-color: ${color('paneBackground')};
        border: 3px solid ${color('rthLegendBorderHover')};
        box-shadow:
          4px 4px 4px -4px ${color('rthLegendBorderHover')},
          4px 4px 4px -4px ${color('rthLegendBorderHover')},
          -4px 4px 4px -4px ${color('rthLegendBorderHover')};
      `
    }
  }}
  `,
  LegendGroup: styled(LegendGroup)`
    display: inline-block;
    vertical-align: top;
    margin-bottom: 15px;
    margin-right: 20px;
  `,
  Time: styled.div`
    width: 100%;
    margin-top: -30px;
    margin-bottom: -5px;
    font-weight: bold;
    font-size: 27px;
    text-align: center;
    color: ${color('rthLegendTime')};
  `,
  AwairInfoPanel: styled(AwairInfoPanel)`
    margin-top: 20px;
  `
}
